import { cachedDashboardGet } from "./dashboardRequestCache";

export type SalesIndividualPeriod =
  | "last_week"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "last_1_year"
  | "custom";

export interface SalesIndividualFilterParams {
  period: SalesIndividualPeriod;
  from_date?: string;
  to_date?: string;
}

export interface SalesIndividualKpiApiItem {
  id: string;
  label: string;
  helper_label?: string;
  helperLabel?: string;
  value: number | string;
}

export interface SalesIndividualKpiResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  kpis: SalesIndividualKpiApiItem[];
  pipeline_stages?: SalesPipelineApiStage[];
}


export interface SalesPipelineApiStage {
  id:
    | "enquiry_received"
    | "quotation_created"
    | "quotation_won"
    | "quotation_lost"
    | "order_own"
    | "dispatched";
  label: string;
  value: number;
  tone?: "default" | "success" | "danger";
}

interface ApiResponseEnvelope<T> {
  status: number;
  success: boolean;
  message: string;
  data: T;
}

const buildSalesIndividualParams = (filters: SalesIndividualFilterParams) => {
  const params: Record<string, string> = {
    period: filters.period,
  };

  if (filters.period === "custom") {
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
  }

  return params;
};

export const fetchSalesIndividualKpis = async (
  filters: SalesIndividualFilterParams,
): Promise<SalesIndividualKpiResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<SalesIndividualKpiResponse>>(
    "/dashboard/sales/individual/kpis",
    buildSalesIndividualParams(filters),
  );

  return response.data;
};

export interface PerformanceTeam {
  id: string;
  name: string;
}

export interface PerformanceMemberRow {
  id: string;
  name: string;
  teamId: string;
  enquiry: number;
  quotationWon: number;
  quotationLost: number;
  order: number;
  orderFromQuotation: number;
  directOrder: number;
  conversionRate: number;
}

export interface PerformanceOverviewResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  teams: PerformanceTeam[];
  members: PerformanceMemberRow[];
}

export const fetchSalesIndividualPerformance = async (
  filters: SalesIndividualFilterParams,
): Promise<PerformanceOverviewResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<PerformanceOverviewResponse>>(
    "/dashboard/sales/individual/performance",
    buildSalesIndividualParams(filters),
  );
  return response.data;
};

export interface InventoryStatusCard {
  id: string;
  label: string;
  value: number;
  helperText: string;
  footerText: string;
  tone: "critical" | "warning" | "success";
}

export interface DispatchPackingRow {
  id: string;
  label: string;
  count: number;
  amountLabel: string;
}

export interface InventoryVendor {
  id: string;
  name: string;
  metrics: {
    purchaseOrdersThisMonth: number;
    pendingVendorDeliveries: number;
    lateVendorDeliveries: number;
  };
}

export interface InventorySnapshotResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  statusCards: InventoryStatusCard[];
  dispatchPacking: DispatchPackingRow[];
  vendors: InventoryVendor[];
}

export const fetchSalesIndividualInventorySnapshot = async (
  filters: SalesIndividualFilterParams,
): Promise<InventorySnapshotResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<InventorySnapshotResponse>>(
    "/dashboard/sales/individual/inventory-snapshot",
    buildSalesIndividualParams(filters),
  );
  return response.data;
};

export interface OperationalInsightItem {
  id: string;
  label: string;
  value: number | string;
  helperLabel: string;
}

export interface OperationalInsightsResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  items: OperationalInsightItem[];
}

export const fetchSalesIndividualOperationalInsights = async (
  filters: SalesIndividualFilterParams,
): Promise<OperationalInsightsResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<OperationalInsightsResponse>>(
    "/dashboard/sales/individual/operational-insights",
    buildSalesIndividualParams(filters),
  );
  return response.data;
};

export interface LowValueCustomerRow {
  id: string;
  customerName: string;
  branch: string;
  industry: string;
  phoneNumber: string;
  companyEmail: string;
  orderCount: number;
  totalOrderValue: number;
}

export interface LowValueCustomersResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  customers: LowValueCustomerRow[];
}

export const fetchSalesIndividualLowValueCustomers = async (
  filters: SalesIndividualFilterParams,
): Promise<LowValueCustomersResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<LowValueCustomersResponse>>(
    "/dashboard/sales/individual/low-value-customers",
    buildSalesIndividualParams(filters),
  );
  return response.data;
};
export interface SalesDashboardBootstrapResponse {
  view: string;
  kpis: SalesIndividualKpiResponse;
  performance: PerformanceOverviewResponse;
  operationalInsights: OperationalInsightsResponse;
  inventory: InventorySnapshotResponse;
  lowValueCustomers: LowValueCustomersResponse;
  charts: unknown | null;
}

export const fetchSalesDashboardBootstrap = async (
  filters: SalesIndividualFilterParams,
  view = "individual",
  comparisonPeriod?: string,
): Promise<SalesDashboardBootstrapResponse> => {
  const params: Record<string, string> = {
    ...buildSalesIndividualParams(filters),
    view,
  };

  if (comparisonPeriod) params.comparison_period = comparisonPeriod;

  const response = await cachedDashboardGet<ApiResponseEnvelope<SalesDashboardBootstrapResponse>>(
    "/dashboard/sales/bootstrap",
    params,
  );

  return response.data;
};
export interface InactiveCustomersResponse {
  period: SalesIndividualPeriod;
  date_range: {
    from_date: string;
    to_date: string;
  };
  customers: any[];
}

export const fetchSalesIndividualInactiveCustomers = async (
  filters: SalesIndividualFilterParams,
  type: "product" | "service",
): Promise<InactiveCustomersResponse> => {
  const response = await cachedDashboardGet<ApiResponseEnvelope<InactiveCustomersResponse>>(
    "/dashboard/sales/individual/inactive-customers",
    {
      ...buildSalesIndividualParams(filters),
      type,
    },
  );
  return response.data;
};
